import type { AxiosInstance } from 'axios';
import type { DashboardData, EmailPreference, LabelPrintLog, Paginated } from '@/types';
import baseAxios from '@/helpers/forms/axios';

interface PrinterInfoPayload {
    fnsku_printer_identifier: string | null
    shipping_printer_identifier: string | null
    fnsku_custom_identifier: string
    shipping_custom_identifier: string
    fnsku_instant_rotate: number
    shipping_instant_rotate: number
}

interface DashboardApi {
    fetchDashboard: () => Promise<DashboardData>
    fetchPrintLogs: (page: number) => Promise<Paginated<LabelPrintLog>>
    saveLabelSizes: (fnskuLabelSizeId: number | null, shippingLabelSizeId: number | null) => Promise<void>
    setMetric: (useMetric: boolean) => Promise<void>
    setFnskuFlip: (flip: boolean) => Promise<void>
    setShippingFlip: (flip: boolean) => Promise<void>
    setPrinterInfo: (payload: PrinterInfoPayload) => Promise<void>
    setPrinterBrand: (brand: string) => Promise<void>
    setEmailPreferences: (preference: EmailPreference) => Promise<void>
    setLabelSizeSet: () => Promise<void>
    setSuccessfulTestPrint: () => Promise<void>
    setOnboardingComplete: () => Promise<void>
}

export default (axios: AxiosInstance = baseAxios): DashboardApi => ({
    fetchDashboard: async (): Promise<DashboardData> => {
        const response = await axios.get<DashboardData>('/api/dashboard');
        return response.data;
    },
    fetchPrintLogs: async (page: number): Promise<Paginated<LabelPrintLog>> => {
        const response = await axios.get<Paginated<LabelPrintLog>>('/api/print-logs', { params: { page } });
        return response.data;
    },
    saveLabelSizes: async (fnskuLabelSizeId: number | null, shippingLabelSizeId: number | null): Promise<void> => {
        await axios.post('/home/label-sizes', {
            fnsku_label_size: fnskuLabelSizeId,
            shipping_label_size: shippingLabelSizeId,
        });
    },
    setMetric: async (useMetric: boolean): Promise<void> => {
        await axios.post('/home/metric', { use_metric: useMetric ? 1 : 0 });
    },
    setFnskuFlip: async (flip: boolean): Promise<void> => {
        await axios.post('/home/flip-fnsku', { flip_fnsku: flip ? 1 : 0 });
    },
    setShippingFlip: async (flip: boolean): Promise<void> => {
        await axios.post('/home/flip-shipping', { flip_shipping: flip ? 1 : 0 });
    },
    setPrinterInfo: async (payload: PrinterInfoPayload): Promise<void> => {
        await axios.post('/home/printer-info', payload);
    },
    setPrinterBrand: async (brand: string): Promise<void> => {
        await axios.post('/home/printer-brand', { printer_brand: brand });
    },
    setEmailPreferences: async (preference: EmailPreference): Promise<void> => {
        await axios.post('/home/email-preferences', { print_email_preferences: preference });
    },
    setLabelSizeSet: async (): Promise<void> => {
        await axios.post('/home/label-size-set');
    },
    setSuccessfulTestPrint: async (): Promise<void> => {
        await axios.post('/home/successful-test-print');
    },
    setOnboardingComplete: async (): Promise<void> => {
        await axios.post('/home/onboarding-complete');
    },
});
